import { useState } from 'react'
import { NUDGGE_API_BASE_URL } from '../../lib/config'

const SHORTLINK_FUNCTIONS = [
  {
    name: 'Shorten-Url',
    description: 'Create shortened URLs with optional custom slugs, TTL, and expiry redirects',
    method: 'POST',
    endpoint: '/functions/v1/Shorten-Url',
    features: [
      'SHA-256 hashed API key authentication',
      'Custom slug support (3-30 chars)',
      'TTL-based expiry (seconds, minutes, hours, days, weeks)',
      'Explicit expiry datetime support',
      'Custom expiry redirect URL',
      'Deterministic or random slug generation'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key via SHA-256 hash lookup in api_credentials table' },
      { step: 2, title: 'Account Lookup', description: 'Loads account config including domain and default expiry settings' },
      { step: 3, title: 'URL Normalization', description: 'Ensures target URL has https:// scheme via normalizeUrl()' },
      { step: 4, title: 'Expiry Resolution', description: 'Resolves expiry from explicit expiresAt > ttl > account default_expiry_hours' },
      { step: 5, title: 'Slug Generation', description: 'Uses custom slug if provided, otherwise deterministic (sha256) or random base62' },
      { step: 6, title: 'Uniqueness Check', description: 'Verifies slug is unique within account scope, retries up to 10 times' },
      { step: 7, title: 'Short URL Construction', description: 'Builds full URL via shortUrlFor() using account domain or edge function path' },
      { step: 8, title: 'Database Insert', description: 'Stores record in short_urls with slug, original_url, expiry, and shortened_url' }
    ],
    curlExample: `curl -X POST \\
  '${NUDGGE_API_BASE_URL}/functions/v1/Shorten-Url' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '{
  "url": "https://example.com/long-page",
  "slug": "my-link",
  "ttl": "7d"
}'`
  },
  {
    name: 'Short-Url-Gen-Keys',
    description: 'Generate API credentials for shortlink accounts (admin only)',
    method: 'POST',
    endpoint: '/functions/v1/Short-Url-Gen-Keys',
    features: [
      'Admin-secret authenticated endpoint',
      'Random 56-character API key generation',
      'SHA-256 key hashing for secure storage',
      'Key prefix extraction for display (first 8 chars)',
      'Account lookup by identifier or domain',
      'One-time plaintext key return'
    ],
    businessLogic: [
      { step: 1, title: 'Admin Auth', description: 'Validates x-admin-secret header against ADMIN_SECRET env var' },
      { step: 2, title: 'Account Resolution', description: 'Looks up account by internal_identifier OR domain field' },
      { step: 3, title: 'Key Generation', description: 'Generates cryptographically random 56-char alphanumeric key' },
      { step: 4, title: 'Hash & Prefix', description: 'SHA-256 hashes full key for storage, extracts first 8 chars as key_prefix' },
      { step: 5, title: 'Credential Insert', description: 'Stores hashed key with account binding in api_credentials table' },
      { step: 6, title: 'Return Plaintext', description: 'Returns plaintext apiKey and prefix (key is never stored or retrievable again)' }
    ],
    curlExample: `curl -X POST \\
  '${NUDGGE_API_BASE_URL}/functions/v1/Short-Url-Gen-Keys' \\
  -H 'Content-Type: application/json' \\
  -H 'x-admin-secret: YOUR_ADMIN_SECRET' \\
  -d '{
  "accountIdentifier": "my-account"
}'`
  },
  {
    name: 'Redirect-Url',
    description: 'Resolve short URL slugs and redirect to original URLs with click tracking',
    method: 'GET',
    endpoint: '/{slug} or /functions/v1/Redirect-Url/{slug}',
    features: [
      'Custom domain routing via request host',
      'Expiry checking with fallback redirect',
      'User-Agent parsing for device analytics',
      'Async click event recording (non-blocking)',
      'Configurable redirect status (301/302)',
      'Cache-Control header support'
    ],
    businessLogic: [
      { step: 1, title: 'Slug Extraction', description: 'Parses slug from the last non-empty URL path segment' },
      { step: 2, title: 'Account Resolution', description: 'Matches request host to account domain, falls back to DEFAULT_ACCOUNT_IDENTIFIER' },
      { step: 3, title: 'URL Lookup', description: 'Queries short_urls by account_id + slug to find the original URL' },
      { step: 4, title: 'Expiry Check', description: 'If expired, redirects to expiry_url or account default, returns 410 if no fallback' },
      { step: 5, title: 'User-Agent Parsing', description: 'Extracts device, browser, OS metadata via ua-parser-js library' },
      { step: 6, title: 'Click Recording', description: 'Inserts click event asynchronously with 100ms timeout to avoid blocking redirect' },
      { step: 7, title: 'HTTP Redirect', description: 'Returns 302 (or configured status) with Location header to original URL' }
    ],
    curlExample: `curl -v \\
  '${NUDGGE_API_BASE_URL}/functions/v1/Redirect-Url/my-link'`
  }
]

const SAMPLE_LOGS = [
  { method: 'POST', status: 200, function: 'Shorten-Url', time: 842, message: 'Short URL created: my-link' },
  { method: 'GET', status: 302, function: 'Redirect-Url', time: 124, message: 'Redirected to original URL' },
  { method: 'POST', status: 200, function: 'Short-Url-Gen-Keys', time: 356, message: 'API key generated' },
  { method: 'POST', status: 400, function: 'Shorten-Url', time: 198, message: 'Missing required field: url' },
  { method: 'POST', status: 401, function: 'Shorten-Url', time: 87, message: 'Invalid API key' },
  { method: 'GET', status: 410, function: 'Redirect-Url', time: 95, message: 'Short URL expired, no fallback' },
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)
  const [logsOpen, setLogsOpen] = useState(false)

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
    if (status >= 300 && status < 400) return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'
    if (status >= 400 && status < 500) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30'
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
  }

  const supabaseLogsUrl = 'https://supabase.com/dashboard/project/ymxdrorugrieltcxycof/logs/edge-functions'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                Edge Functions
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Shortlink Supabase Edge Functions documentation and logs
              </p>
            </div>
          </div>
          <a
            href={supabaseLogsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span>Open Supabase Logs</span>
          </a>
        </div>
      </div>

      {/* Live Logs Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button
          onClick={() => setLogsOpen(!logsOpen)}
          className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Live Logs</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">View edge function execution logs (last 24 hours)</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${logsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {logsOpen && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            {/* Info banner */}
            <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    For detailed logs with full request/response data, use the Supabase Dashboard.
                    The preview below shows the log format you'll see.
                  </p>
                  <a
                    href={supabaseLogsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 mt-2"
                  >
                    <span>Open Supabase Logs Explorer</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Sample logs preview */}
            <div className="px-4 sm:px-6 py-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Log Format Preview</h4>
              <div className="space-y-2 font-mono text-xs">
                {SAMPLE_LOGS.map((log, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700"
                  >
                    <span className={`px-2 py-0.5 rounded font-semibold ${getStatusColor(log.status)}`}>
                      {log.status}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 w-12">{log.method}</span>
                    <span className="text-purple-600 dark:text-purple-400 font-medium flex-shrink-0">
                      {log.function}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 flex-1 truncate">
                      {log.message}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {log.time}ms
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* What to look for */}
            <div className="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Status Code Reference</h4>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                    200
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Success</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    302
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Redirect</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                    400/401/410
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Client Error</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                    500
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Server Error</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Function List */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-1">Available Functions</h3>
          {SHORTLINK_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                      {func.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      {func.method}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {func.description}
                  </p>
                  <code className="text-xs text-purple-600 dark:text-purple-400 mt-2 block">
                    {func.endpoint}
                  </code>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Function Details */}
        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Function Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-purple-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                    {selectedFunction.method}
                  </span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">
                    {selectedFunction.endpoint}
                  </code>
                </div>
              </div>

              {/* Features */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business Logic Flow */}
              <div className="p-4 sm:p-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Business Logic Flow
                </h4>
                <div className="space-y-3">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div
                      key={idx}
                      className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {logic.step}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {logic.title}
                        </h5>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {logic.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Example */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  API Example
                </h4>
                <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{selectedFunction.curlExample}</code>
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Function
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
